/**
 * countryGeo.ts — sanctioned home for country geographic seed data
 * (issue #378). The admin-managed operational_cities table is the runtime
 * source of truth for city lists (see useOperationalRegions/useCountryConfig);
 * the data below is the fallback seed plus town/region detail the table
 * doesn't model yet. Moved verbatim from the components that used to hold
 * private copies (LocationSelector, HelperProfileCreation, haversine, …).
 *
 * This file (and countryConfig.ts) are the ONLY places in src/ allowed to
 * hold country literals — enforced by assert-country-literal-ratchet.mjs.
 */

/** Fallback city list for the default country when the DB is unreachable. */
export const DEFAULT_CITY_FALLBACK = [
  'Kampala', 'Entebbe', 'Jinja', 'Mbarara', 'Gulu', 'Lira', 'Mbale', 'Masaka', 'Arua', 'Wakiso',
];

/** Region → major-city cascade for the default country. */
export const DEFAULT_REGIONS = [
  'Central Region', 'Eastern Region', 'Northern Region', 'Western Region',
];

export const MAJOR_CITIES_BY_REGION: Record<string, string[]> = {
  'Central Region': ['Kampala', 'Entebbe', 'Mukono', 'Wakiso', 'Mpigi'],
  'Eastern Region': ['Jinja', 'Mbale', 'Soroti', 'Tororo', 'Iganga'],
  'Northern Region': ['Gulu', 'Lira', 'Arua', 'Kitgum', 'Pader'],
  'Western Region': ['Mbarara', 'Fort Portal', 'Kasese', 'Hoima', 'Masindi'],
};

/** City → division/suburb cascade (region-picker granularity). */
export const DIVISIONS_BY_CITY: Record<string, string[]> = {
  'Kampala': ['Nakawa', 'Kawempe', 'Makindye', 'Rubaga', 'Central Division'],
  'Entebbe': ['Entebbe Municipality', 'Kitoro', 'Bugonga', 'Nakiwogo'],
  'Jinja': ['Jinja Central', 'Budondo', 'Kimaka', 'Walukuba'],
  'Mbale': ['Industrial Division', 'Northern Division', 'Wanale'],
  'Mbarara': ['Kakoba Division', 'Kamukuzi Division', 'Nyamitanga Division'],
  'Gulu': ['Bardege Division', 'Layibi Division', 'Laroo Division'],
  'Arua': ['Oli Division', 'Tanganyika Division', 'River Oli Division'],
  'Fort Portal': ['Central Division', 'East Division', 'South Division'],
};

/** Multi-country city lists (used by profile-creation cascades). */
export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  UG: ['Kampala', 'Entebbe', 'Jinja', 'Mbarara', 'Gulu', 'Lira', 'Mbale', 'Masaka', 'Arua', 'Soroti'],
  KE: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi', 'Kitale', 'Garissa', 'Nyeri'],
  TZ: ['Dar es Salaam', 'Dodoma', 'Mwanza', 'Arusha', 'Mbeya', 'Morogoro', 'Tanga', 'Zanzibar City', 'Kigoma', 'Iringa'],
  RW: ['Kigali', 'Huye', 'Muhanga', 'Musanze', 'Rubavu', 'Rusizi', 'Nyagatare', 'Karongi'],
  US: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'Austin'],
  GB: ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool', 'Bristol', 'Sheffield', 'Edinburgh', 'Cardiff'],
};

export const COUNTRY_NAMES: Record<string, string> = {
  UG: 'Uganda', KE: 'Kenya', TZ: 'Tanzania', RW: 'Rwanda', US: 'United States', GB: 'United Kingdom',
};

/** City → town/suburb cascade per country (profile-creation granularity). */
export const TOWNS_BY_COUNTRY: Record<string, Record<string, string[]>> = {
  UG: {
    Kampala: ['Kololo', 'Nakasero', 'Ntinda', 'Bukoto', 'Kawempe', 'Makindye', 'Rubaga', 'Kisaasi', 'Kyanja', 'Bugolobi', 'Muyenga', 'Naguru', 'Other'],
    Entebbe: ['Entebbe Town', 'Katabi', 'Kajjansi', 'Nakiwogo', 'Other'],
    Jinja: ['Jinja Town', 'Walukuba', 'Mpumudde', 'Kimaka', 'Bugembe', 'Other'],
    Mbarara: ['Mbarara Town', 'Kakiika', 'Nyamitanga', 'Kamukuzi', 'Other'],
    Gulu: ['Gulu Town', 'Laroo', 'Pece', 'Bardege', 'Other'],
    Lira: ['Lira Town', 'Adyel', 'Central', 'Ojwina', 'Other'],
    Mbale: ['Mbale Town', 'Industrial', 'Other'],
    Masaka: ['Masaka Town', 'Nyendo', 'Kimaanya', 'Other'],
    Arua: ['Arua Town', 'Ayivu', 'Vurra', 'Other'],
    Soroti: ['Soroti Town', 'Gweri', 'Other'],
  },
  KE: {
    Nairobi: ['Westlands', 'Karen', 'Kilimani', 'Eastleigh', 'Kasarani', 'Embakasi', 'Langata', 'Lavington', 'Runda', 'Other'],
    Mombasa: ['Nyali', 'Bamburi', 'Kizingo', 'Old Town', 'Likoni', 'Other'],
    Kisumu: ['Milimani', 'Nyalenda', 'Kondele', 'Mamboleo', 'Other'],
    Nakuru: ['Milimani', 'Section 58', 'Free Area', 'Racecourse', 'Other'],
    Eldoret: ['Elgon View', 'Kimumu', 'Pioneer', 'Langas', 'Other'],
    Thika: ['Makongeni', 'Kiganjo', 'Landless', 'Other'],
    Malindi: ['Malindi Town', 'Shella', 'Watamu Road', 'Other'],
    Kitale: ['Kitale Town', 'Milimani', 'Other'],
    Garissa: ['Garissa Town', 'Bura', 'Other'],
    Nyeri: ['Nyeri Town', 'Kiganjo', 'Other'],
  },
  TZ: {
    'Dar es Salaam': ['Msasani', 'Oyster Bay', 'Kinondoni', 'Ilala', 'Temeke', 'Mikocheni', 'Other'],
    Dodoma: ['Dodoma Town', 'Area C', 'Chamwino', 'Other'],
    Mwanza: ['Ilemela', 'Nyamagana', 'Other'],
    Arusha: ['Arusha Town', 'Njiro', 'Kimandolu', 'Other'],
    Mbeya: ['Mbeya Town', 'Iyunga', 'Other'],
    Morogoro: ['Morogoro Town', 'Kihonda', 'Other'],
    Tanga: ['Tanga Town', 'Other'],
    'Zanzibar City': ['Stone Town', 'Michenzani', 'Other'],
    Kigoma: ['Kigoma Town', 'Other'],
    Iringa: ['Iringa Town', 'Other'],
  },
  RW: {
    Kigali: ['Kimihurura', 'Kacyiru', 'Nyarutarama', 'Remera', 'Kimironko', 'Nyamirambo', 'Other'],
    Huye: ['Huye Town', 'Other'],
    Muhanga: ['Muhanga Town', 'Other'],
    Musanze: ['Musanze Town', 'Other'],
    Rubavu: ['Gisenyi', 'Other'],
    Rusizi: ['Kamembe', 'Other'],
    Nyagatare: ['Nyagatare Town', 'Other'],
    Karongi: ['Kibuye', 'Other'],
  },
  US: {
    'New York': ['Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island', 'Other'],
    'Los Angeles': ['Downtown LA', 'Hollywood', 'Santa Monica', 'Venice', 'Other'],
    Chicago: ['Loop', 'Lincoln Park', 'Wicker Park', 'Other'],
    Houston: ['Downtown', 'Midtown', 'The Heights', 'Other'],
    Phoenix: ['Downtown Phoenix', 'Scottsdale Area', 'Tempe Area', 'Other'],
    Philadelphia: ['Center City', 'South Philly', 'University City', 'Other'],
    'San Antonio': ['Downtown', 'Alamo Heights', 'Other'],
    'San Diego': ['Downtown', 'La Jolla', 'Pacific Beach', 'Other'],
    Dallas: ['Downtown', 'Uptown', 'Deep Ellum', 'Other'],
    Austin: ['Downtown', 'South Congress', 'East Austin', 'Other'],
  },
  GB: {
    London: ['Camden', 'Islington', 'Hackney', 'Greenwich', 'Westminster', 'Other'],
    Manchester: ['City Centre', 'Didsbury', 'Salford', 'Other'],
    Birmingham: ['City Centre', 'Edgbaston', 'Other'],
    Leeds: ['City Centre', 'Headingley', 'Other'],
    Glasgow: ['City Centre', 'West End', 'Other'],
    Liverpool: ['City Centre', 'Wavertree', 'Other'],
    Bristol: ['City Centre', 'Clifton', 'Other'],
    Sheffield: ['City Centre', 'Ecclesall', 'Other'],
    Edinburgh: ['Old Town', 'New Town', 'Other'],
    Cardiff: ['City Centre', 'Cathays', 'Other'],
  },
};

/**
 * Approximate lat/lng centres for the default country's major cities.
 * Fallback when helpers do not yet have stored coordinates.
 */
export const CITY_COORDS: Record<string, [number, number]> = {
  kampala: [0.3476, 32.5825],
  entebbe: [0.0512, 32.4637],
  jinja: [0.4244, 33.2041],
  gulu: [2.7745, 32.2990],
  mbarara: [-0.6072, 30.6545],
  mbale: [1.0800, 34.1754],
  lira: [2.2499, 32.8999],
  arua: [3.0206, 30.9111],
  fort_portal: [0.6710, 30.2750],
  masaka: [-0.3333, 31.7333],
};
